import { Checkbox, Table, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import { FC, useMemo } from "react";
import { shallowEqual, useDispatch } from "react-redux";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import useResize from "hooks/useResize";
import { scheduleSetFilter, selectAllUsersList } from "actions/scheduleActions";
import { getFilteredScheduleIds, getScheduleColumnDate } from "utils/scheduleHelpers";
import { getScheduleTableColumns } from "./scheduleTable";
import { ScheduleHeadCell } from "./ScheduleHeadCell";
import { ScheduleRow } from "./ScheduleRow";
import { useStyles } from "./styles";
import { ScheduleUserData } from "types/Schedule";
import ScheduleTableFooter from "./ScheduleTableFooter";

interface PropsType {
  tableNumber: number;
}

const ScheduleTable: FC<PropsType> = ({ tableNumber }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { width } = useResize();
  const currentUserRole = useTypeSelectors((state) => state.auth.userData.role);
  const { userList, dataList, dateList, planList, idListForDelete, idListForSMS, scheduleFilters } =
    useTypeSelectors((state) => state.scheduleTable, shallowEqual);

  const { activeColumns, isTwoWeekTable, ordering, search, shiftRoles, station, userRoles } =
    scheduleFilters;
  const sortId = ordering || "-id";

  const rowIds = useMemo(
    () =>
      getFilteredScheduleIds(userList, userRoles, shiftRoles, search, sortId, dataList, station),
    [userList, userRoles, shiftRoles, search, sortId, dataList, station],
  );

  const columns = useMemo(
    () =>
      getScheduleTableColumns(activeColumns, isTwoWeekTable, tableNumber, width).map((col) => ({
        ...col,
        date: getScheduleColumnDate(dateList, col.label),
      })),
    [activeColumns, isTwoWeekTable, dateList, tableNumber, width],
  );
  const headerColumns = useMemo(() => columns.filter((col) => col.id !== "checkbox"), [columns]);

  const rows = useMemo(() => {
    const usersById = new Map(userList.map((user) => [user.id, user]));
    return rowIds.reduce<ScheduleUserData[]>((acc, id) => {
      const user = usersById.get(id);
      if (user) acc.push(user);
      return acc;
    }, []);
  }, [rowIds, userList]);

  const handleSortChange = (currentSort: string | undefined, cellId: string) => {
    const nextSort =
      currentSort?.replace("-", "") === cellId
        ? `${currentSort?.includes("-") ? "" : "-"}${cellId}`
        : cellId;
    dispatch(scheduleSetFilter({ ordering: nextSort }));
  };

  return (
    <Table stickyHeader>
      <TableHead>
        <TableRow>
          {tableNumber === 1 && (
            <TableCell
              className={`${classes.tableHeadCheckCell} ${classes.fixedHeightOne} ${classes.tableBorderRight}`}
            >
              <Checkbox
                onChange={() => dispatch(selectAllUsersList())}
                checked={userList.length === idListForSMS?.length}
                className={classes.selectCheckbox}
              />
            </TableCell>
          )}
          {headerColumns.map((col, index) => (
            <ScheduleHeadCell
              key={col.id}
              col={col}
              index={index}
              classes={classes}
              sortId={sortId}
              station={station}
              dataList={dataList}
              planList={planList}
              onSortChange={handleSortChange}
            />
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((user) => (
          <ScheduleRow
            key={user.id}
            user={user}
            columns={columns}
            classes={classes}
            currentUserRole={currentUserRole}
            idListForSMS={idListForSMS}
            idListForDelete={idListForDelete}
            isSelected={idListForSMS?.includes(user.id)}
            tableNumber={tableNumber}
          />
        ))}
      </TableBody>
      <ScheduleTableFooter tableNumber={tableNumber} headerColumns={headerColumns} rows={rows} />
    </Table>
  );
};

export default ScheduleTable;
